import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../contexts/ToastContext';
import { integrationApiService } from '../../../services/integrationApi';
import type { IntegrationConfig, ProductMatchResult, ShopifyScanJob } from '../../../services/integrationApi';

/** How often the scan job is asked how far it got while it is queued or running. */
export const SCAN_POLL_MS = 2500;
/** Consecutive failed status reads, while a job is live, before the view says it cannot reach it. */
export const STATUS_ERRORS_SHOWN = 3;

export interface ShopifyScanJobState {
  /** The scan result on screen: the integration's last one until a fresh one is adopted. */
  result: ProductMatchResult | null;
  /** Identity (run id, else finish time) of the scan shown, for keying data derived from it. */
  runKey: string | null;
  job: ShopifyScanJob | undefined;
  /** Starting the job, or the job is queued or running. */
  scanning: boolean;
  running: boolean;
  /** Status reads keep failing while a job is live. */
  statusUnreachable: boolean;
  scan: () => Promise<void>;
  /** Replace the shown result (after the store links matches, so the counts stay true). */
  setResult: (next: ProductMatchResult | null) => void;
}

/**
 * The Shopify catalog scan as a background job: POST queues it, a status poll
 * reports progress, and a finished run is adopted by its identity. Same
 * behaviour as `ShopifyCatalogScan` (run-identity adoption, polling through
 * transient status errors, a POST that races the worker's claim), kept in one
 * hook so the v2 view does not carry a second copy of it.
 */
export function useShopifyScanJob(integration: IntegrationConfig): ShopifyScanJobState {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [result, setResult] = useState<ProductMatchResult | null>(integration.settings.lastMatchResult ?? null);
  const [runKey, setRunKey] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  // Set once this view has seen the scan queued or running (or queued it itself), so a result it waited for refreshes the integration queries.
  const following = useRef(false);
  // Identity of the finished scan this view last adopted.
  const shownRun = useRef<string | null>(null);

  // A different integration starts from its own last result, never the previous one's.
  useEffect(() => {
    setResult(integration.settings.lastMatchResult ?? null);
    setRunKey(null);
    following.current = false;
    shownRun.current = null;
    // Reseed on the integration id only: a refetch must not wipe a fresh scan.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integration.id]);

  const statusKey = ['shopify-scan-status', integration.id];
  const statusQuery = useQuery({
    queryKey: statusKey,
    queryFn: () => integrationApiService.getScanStatus(integration.id),
    refetchInterval: (query) => {
      const state = query.state.data?.status;
      return state === 'queued' || state === 'running' ? SCAN_POLL_MS : false;
    },
    // A transient failure while polling must not end the scan; the next tick asks again.
    retry: 1,
  });
  const job = statusQuery.data;
  const jobStatus = job?.status;
  const running = jobStatus === 'queued' || jobStatus === 'running';
  const statusUnreachable = running && statusQuery.errorUpdateCount >= STATUS_ERRORS_SHOWN;

  useEffect(() => {
    if (running) {
      following.current = true;
      return;
    }
    if (starting) return;
    if (jobStatus === 'done') {
      const observed = following.current;
      following.current = false;
      // Reconcile by run identity, not by having watched it run: a view that mounts after the
      // scan finished (tab change, long absence) must show the fresh result too.
      const identity = job?.runId ?? job?.finishedAt ?? null;
      if (!job?.result || !job.result.matched) {
        if (observed) showToast('The scan returned no match data.', 'error');
        return;
      }
      if (identity && identity === shownRun.current) return;
      shownRun.current = identity;
      setRunKey(identity);
      // The same content already on screen (remount with current props) needs no reload.
      if (result && JSON.stringify(result) === JSON.stringify(job.result)) return;
      setResult(job.result);
      if (observed) {
        queryClient.invalidateQueries({ queryKey: ['integration', integration.id] });
        queryClient.invalidateQueries({ queryKey: ['integrations'] });
      }
    } else if (jobStatus === 'failed') {
      following.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobStatus, running, starting, job?.runId, job?.finishedAt]);

  const scan = async () => {
    setStarting(true);
    following.current = true;
    try {
      await integrationApiService.scanProducts(integration.id);
    } catch (error) {
      following.current = false;
      showToast(error instanceof Error ? error.message : 'The scan failed. Try again.', 'error');
      setStarting(false);
      return;
    }
    // The job is queued. Record that first, so polling is live whatever the read below does.
    following.current = true;
    // A read already in flight predates the POST and could land after this and say "none".
    await queryClient.cancelQueries({ queryKey: statusKey });
    queryClient.setQueryData<ShopifyScanJob>(statusKey, (prev) => ({
      status: 'queued',
      runId: null,
      progress: { pagesRead: 0, productsSeen: 0 },
      error: null,
      requestedAt: new Date().toISOString(),
      startedAt: null,
      finishedAt: null,
      result: null,
      ...(prev && (prev.status === 'queued' || prev.status === 'running') ? prev : {}),
    }));
    try {
      // Ask straight away: an inline run is already done, and a queued one shows its first progress.
      const first = await integrationApiService.getScanStatus(integration.id);
      // A read that races the worker's claim can still say "none"; the queued state stands then.
      if (first.status !== 'none') queryClient.setQueryData(statusKey, first);
    } catch {
      // Transient: the job is queued and the interval keeps asking.
    } finally {
      setStarting(false);
    }
  };

  return { result, runKey, job, scanning: starting || running, running, statusUnreachable, scan, setResult };
}
